import { useState, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  CircularProgress,
  Stack,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { observer } from 'mobx-react-lite';
import { useCalendarAuthStore } from '../../stores/root_store';
import type { CalendarListItem } from '../../types/calendar';

interface CalendarListProps {
  identity: string;
}

const CalendarList = observer(({ identity }: CalendarListProps) => {
  const calendarAuthStore = useCalendarAuthStore();
  const [fetching, setFetching] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (!identity) return;

    setFetching(true);
    calendarAuthStore
      .fetchAvailableCalendars(identity)
      .finally(() => setFetching(false));
  }, [identity, calendarAuthStore]);

  const calendars = calendarAuthStore.availableCalendars;
  const syncedIds = new Set(calendarAuthStore.syncedCalendars);

  async function handleToggleCalendar(cal: CalendarListItem, checked: boolean) {
    setSavingId(cal.id);
    try {
      if (checked) {
        await calendarAuthStore.addSyncedCalendar(identity, cal.id);
      } else {
        await calendarAuthStore.removeSyncedCalendar(identity, cal.id);
      }
    } finally {
      setSavingId(null);
    }
  }

  if (fetching) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" p={2}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading calendars...</Typography>
      </Stack>
    );
  }

  if (calendars.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" p={2}>
        No calendars found
      </Typography>
    );
  }

  return (
    <List dense>
      {calendars.map((cal) => (
        <ListItem key={cal.id}>
          <ListItemIcon>
            {savingId === cal.id ? (
              <CircularProgress size={20} sx={{ ml: 1.25, mr: 1.25 }} />
            ) : (
              <Checkbox
                edge="start"
                checked={cal.primary || syncedIds.has(cal.id)}
                onChange={(e) => handleToggleCalendar(cal, e.target.checked)}
                disabled={savingId !== null || cal.primary}
              />
            )}
          </ListItemIcon>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <CalendarMonthIcon sx={{ color: cal.backgroundColor }} />
          </ListItemIcon>
          <ListItemText
            primary={cal.summaryOverride || cal.summary}
            secondary={cal.access_role === 'reader' ? 'Read only' : undefined}
            sx={{
              opacity: cal.access_role === 'reader' ? 0.6 : 1,
            }}
          />
        </ListItem>
      ))}
    </List>
  );
});

export default CalendarList;
